// Copyright (c) StrangeBee 2024

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { TitleSection } from '@Atoms/TitleSection/TitleSection';
import { ICONS } from '@Constants/iconsApp';
import { useLocalMessage } from '@Helpers/locale';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THInputNumber } from '@Molecules/Inputs/THInputNumber/THInputNumber';
import { THPassword } from '@Molecules/Inputs/THPassword/THPassword';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { THForm } from '@Molecules/THForm/THForm';
import { FormInstance } from 'antd';
import React, { ReactElement, useEffect } from 'react';
import { FieldMapType, LDAPServerType, OrganisationOutput, ProfileOutput } from 'thehive-sdk';
import { THTooltip } from '@Molecules/THTooltip/THTooltip';

import './LdapServerForm.scss';

const FIELD_NAME = {
    name: 'name',
    hosts: 'hosts',
    useSSL: 'useSSL',
    bindDN: 'bindDN',
    bindPW: 'bindPW',
    baseDN: 'baseDN',
    filter: 'filter',
    searchScope: 'searchScope',
    pageSize: 'pageSize',
    fieldMap: {
        root: 'fieldMap',
        login: 'login',
        name: 'name',
        memberOf: 'memberOf',
        email: 'email',
        locked: 'locked',
        apikey: 'apiKey',
        totpSecret: 'totpSecret',
        type: 'type',
    },
    groupMappings: { root: 'groupMappings', group: 'group', organisation: 'organisation', profile: 'profile' },
};

export interface LdapServerFormOutput {
    name: string;
    hosts: string[];
    useSSL: boolean;
    bindDN: string;
    bindPW: string;
    baseDN: string;
    filter: string;
    searchScope: string;
    pageSize: number;
    fieldMap: FieldMapType;
    groupMappings: GroupMappingType[];
}

export type MemberMapOutputType = Record<
    string,
    {
        organisation: string;
        profile: string;
    }
>;
export type MemberMapInputType = {
    map: string;
    organisation: string;
    profile: string;
};

export type GroupMappingType = {
    group: string;
    organisation: string;
    profile: string;
};

interface LdapServerFormProps {
    form: FormInstance;
    isValid: boolean;
    defaultValues?: LDAPServerType;
    namesList?: string[];
    profileList?: ProfileOutput[];
    organisationList?: OrganisationOutput[];

    onFinish: (server: LdapServerFormOutput) => Promise<void>;
    onChange: () => void;
    onCancel: () => void;
}

export const LdapServerForm = ({
    form,
    defaultValues,
    namesList = [],
    profileList = [],
    organisationList = [],
    onFinish,
    onChange,
    isValid,
    onCancel,
}: LdapServerFormProps): ReactElement => {
    const local = useLocalMessage();
    const [initialValues, setInitialValues] = React.useState<LdapServerFormOutput>({} as LdapServerFormOutput);

    const parseMemberMapOutput = (memberMap: MemberMapOutputType) =>
        Object.entries(memberMap).reduce<MemberMapInputType[]>((acc, [key, value]) => {
            return [...acc, { map: key, ...value }];
        }, [] as MemberMapInputType[]);

    useEffect((): void => {
        if (defaultValues) {
            const memberMap =
                defaultValues.memberMap &&
                Object.keys(defaultValues.memberMap).length !== 0 &&
                parseMemberMapOutput(defaultValues.memberMap as MemberMapOutputType);

            const groupMappings = memberMap
                ? memberMap.map(({ map, organisation, profile }) => {
                      return { group: map, organisation, profile };
                  })
                : defaultValues.groupMappings;

            setInitialValues({ ...defaultValues, groupMappings });
        }
    }, [defaultValues]);

    useEffect(() => {
        form.resetFields();
    }, [initialValues]); //eslint-disable-line

    return (
        <THForm
            form={form}
            initialValues={initialValues}
            onValuesChange={onChange}
            onFinish={onFinish}
            autoComplete="off"
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} />}>
                <div className="form__section">
                    <TitleSection>{local('configPlateform.section.principal')}</TitleSection>
                    <THForm.Item
                        name={[FIELD_NAME.name]}
                        label={local('configPlateform.ldap.config-name')}
                        rules={[
                            {
                                required: true,
                                message: local('configPlateform.required.general'),
                                validator: (_, value) => {
                                    if (!!defaultValues && defaultValues.name === value) {
                                        return Promise.resolve();
                                    }
                                    if (namesList?.includes(value) || value === '') {
                                        return Promise.reject(local('common.already-exists'));
                                    }
                                    if (!value?.trim()) {
                                        return Promise.reject(local('common.required'));
                                    }
                                    return Promise.resolve();
                                },
                            },
                        ]}
                    >
                        <THInput placeholder={'Name'} disabled={!!defaultValues} />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.hosts]}
                        label={local('configPlateform.ldap.hosts')}
                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                    >
                        <THSelect mode="tags" />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.useSSL]}
                        label={local('configPlateform.ldap.useSSL')}
                        valuePropName="checked"
                    >
                        <THSwitch />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.bindDN]}
                        label={local('configPlateform.ldap.bindDN')}
                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                    >
                        <THInput placeholder="cn=admin,dc=myorg,dc=com" autoComplete="new-password" />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.bindPW]}
                        label={local('configPlateform.ldap.bindPW')}
                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                    >
                        <THPassword autoComplete="new-password" />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.baseDN]}
                        label={local('configPlateform.ldap.baseDN')}
                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                    >
                        <THInput placeholder="dc=myorg,dc=com" />
                    </THForm.Item>

                    <THForm.Item name={[FIELD_NAME.filter]} label={local('configPlateform.ldap.filter')}>
                        <THInput placeholder="(objectClass=inetOrgPerson)" />
                    </THForm.Item>

                    <THForm.Item name={[FIELD_NAME.searchScope]} label={local('configPlateform.ldap.search-scope')}>
                        <THInput placeholder="subtree" />
                    </THForm.Item>

                    <THForm.Item name={[FIELD_NAME.pageSize]} label={local('configPlateform.ldap.page-size')}>
                        <THInputNumber />
                    </THForm.Item>
                </div>

                <div className="form__section">
                    <TitleSection>{local('configPlateform.ldap.map-attributes.section-title')}</TitleSection>
                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.login]}
                        label={local('configPlateform.ldap.map-attributes.map-login')}
                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                    >
                        <THInput placeholder={'uid'} />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.name]}
                        label={local('configPlateform.ldap.map-attributes.map-name')}
                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                    >
                        <THInput placeholder={'cn'} />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.memberOf]}
                        label={local('configPlateform.ldap.map-attributes.map-member-of')}
                        rules={[{ required: true, message: local('configPlateform.required.general') }]}
                    >
                        <THInput placeholder={'memberOf'} />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.email]}
                        label={local('configPlateform.ldap.map-attributes.map-email')}
                    >
                        <THInput placeholder={'mail'} />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.locked]}
                        label={local('configPlateform.ldap.map-attributes.map-locked')}
                    >
                        <THInput placeholder={'PwdAccountLockedTime'} />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.apikey]}
                        label={local('configPlateform.ldap.map-attributes.map-apikey')}
                    >
                        <THInput />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.totpSecret]}
                        label={local('configPlateform.ldap.map-attributes.map-secret')}
                    >
                        <THInput />
                    </THForm.Item>

                    <THForm.Item
                        name={[FIELD_NAME.fieldMap.root, FIELD_NAME.fieldMap.type]}
                        label={local('configPlateform.ldap.map-attributes.map-type')}
                    >
                        <THInput placeholder="normal" />
                    </THForm.Item>
                </div>

                <div className="form__section">
                    <TitleSection>{local('configPlateform.ldap.map-groups.section-title')}</TitleSection>
                    <THForm.List name={FIELD_NAME.groupMappings.root}>
                        {(fields, { add, remove }) => (
                            <div className="map-group__section">
                                {fields.map((field, index) => (
                                    <div key={index} className="map-group__section-field">
                                        <THForm.Item
                                            name={[field.name, FIELD_NAME.groupMappings.group]}
                                            className="map-group__section-field-map"
                                            label={
                                                <>
                                                    <span>{local('configPlateform.ldap.map-groups.map-group')}</span>
                                                    <THTooltip
                                                        title={local('configPlateform.ldap.map-groups.remove-group')}
                                                    >
                                                        <THButton
                                                            type="link"
                                                            onClick={() => {
                                                                onChange();
                                                                remove(field.name);
                                                            }}
                                                            className="map-group__button-minus"
                                                        >
                                                            <ICONS.DeleteIcon />
                                                        </THButton>
                                                    </THTooltip>
                                                </>
                                            }
                                        >
                                            <THInput placeholder="cn=soc-ro,ou=groups,dc=myorg,dc=com" />
                                        </THForm.Item>

                                        <THForm.Item
                                            name={[field.name, FIELD_NAME.groupMappings.organisation]}
                                            label={local('configPlateform.ldap.map-groups.organisation')}
                                        >
                                            <THSelect
                                                options={organisationList.map(({ name }) => ({
                                                    label: name,
                                                    value: name,
                                                }))}
                                            />
                                        </THForm.Item>

                                        <THForm.Item noStyle shouldUpdate>
                                            {({ getFieldValue }) => (
                                                <THForm.Item
                                                    name={[field.name, FIELD_NAME.groupMappings.profile]}
                                                    label={local('configPlateform.ldap.map-groups.profile')}
                                                >
                                                    <THSelect
                                                        options={profileList
                                                            .filter((profile) =>
                                                                getFieldValue(FIELD_NAME.groupMappings.root)?.[
                                                                    field.name
                                                                ]?.[FIELD_NAME.groupMappings.organisation] === 'admin'
                                                                    ? profile.forAdmin
                                                                    : profile.forOrg,
                                                            )
                                                            .map(({ name }) => ({ label: name, value: name }))}
                                                    />
                                                </THForm.Item>
                                            )}
                                        </THForm.Item>
                                    </div>
                                ))}
                                <THButton
                                    type="default"
                                    className="map-group__button-plus"
                                    onClick={() => {
                                        onChange();
                                        add();
                                    }}
                                    icon={<ICONS.PlusCircleOutlined />}
                                >
                                    {local('configPlateform.ldap.map-groups.add-group')}
                                </THButton>
                            </div>
                        )}
                    </THForm.List>
                </div>
            </THForm.Layout>
        </THForm>
    );
};
